<template>
	<Col class="width-100 pt-2">
		<Row>
			<h2>Ply URL</h2>
		</Row>
		<Row>
			<LinkCopy :src="plyLink" class="width-100" />
		</Row>

		<template v-if="isPortal && isCustomDomainsEnabled">
			<MaybeUpgradeRequiredIndicator :billingFeatureId="billingFeatureId">
				<template #default="{ indicator, isUpgradeRequired }">
					<Row class="flex-justify-space-between mt-5 mr-1">
						<h2>Custom URL</h2>
						<Row class="flex-justify-space-between">
							<a class="fs-14 purple-text" @click="(e) => editCustomURL(e, isUpgradeRequired, indicator)" href="#"
								>{{ customURLExists ? 'Edit' : 'Set Custom' }} URL</a
							>
							<div class="pl-2" v-if="billingFeatureId && isUpgradeRequired">
								<component :is="indicator" :billingFeatureId="billingFeatureId" iconOnly />
							</div>
						</Row>
					</Row>
				</template>
			</MaybeUpgradeRequiredIndicator>
			<Row v-if="customURLExists">
				<LinkCopy :src="customURL" class="width-100" />
			</Row>
		</template>

		<div class="mt-5" v-if="isPortal && usePortalAuthEnabled">
			<PortalAuthSettingsContent :settings.sync="localCustomerAuthSettings" />
		</div>

		<div>
			<ParamOptionSettings style="width: 102%" @updateOptions="updateOptions" />
		</div>
	</Col>
</template>

<script>
import debounce from 'lodash/debounce';

import ParamOptionSettings from '../ParamOptionSettings.vue';
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import LinkCopy from '@/components/common/LinkCopy/LinkCopy.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import PortalAuthSettingsContent from '@/components/dialogs/PortalAuthSettingsDialog/PortalAuthSettingsContent.vue';
import { isCustomDomainsEnabled, usePortalAuthEnabled } from '@/store/modules/featureFlags/getters';
import { getNode } from '@/store/modules/nodes/getters';
import { partialUpdateWorkflowVersionSettings } from '@/store/modules/selectedWorkflow/actions';
import { isPortal, customerAuthSettings, editedWorkflowVersion, isCustomerAuthOn } from '@/store/modules/selectedWorkflow/getters';
import { setIsSideBarDrawerOpen, setIsShowKnob } from '@/store/modules/sideBarDrawer/actions';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';

export default {
	name: 'URLToolPlacementEditor',
	props: { workflow: { type: Object } },
	components: { ParamOptionSettings, LinkCopy, MaybeUpgradeRequiredIndicator, PortalAuthSettingsContent },
	computed: {
		...mapGettersShorthand({
			isCustomDomainsEnabled,
			isPortal,
			customerAuthSettings,
			editedWorkflowVersion,
			usePortalAuthEnabled,
			isCustomerAuthOn,
			getNode,
		}),
		billingFeatureId() {
			return FeatureTypes.CustomerCustomDomains;
		},
		plyLink() {
			const node = this.getNode(this.workflow.current_draft.root_node);
			const params = Object.fromEntries(
				Object.keys(node.params_map.url_parameters || {}).map((key) => [key, node.test_result.url_parameters?.[key] || 'xxxx'])
			);
			return WorkflowService.buildTriggerURL(this.workflow.ply_run_link, params).href;
		},
		customURL() {
			const node = this.getNode(this.workflow.current_draft.root_node);
			const params = Object.fromEntries(
				Object.keys(node.params_map.url_parameters || {}).map((key) => [key, node.test_result.url_parameters?.[key] || 'xxxx'])
			);
			return WorkflowService.buildTriggerURL(this.workflow.run_link, params).href;
		},
		customURLExists() {
			return !!this.workflow.tool_preferences.custom_domain;
		},
		localCustomerAuthSettings: {
			get() {
				return this.customerAuthSettings;
			},
			set: debounce(async function (value) {
				this.partialUpdateWorkflowVersionSettings(value);
			}, 300),
		},
	},
	methods: {
		...mapActionsShorthand({
			partialUpdateWorkflowVersionSettings,
			setIsSideBarDrawerOpen,
			setIsShowKnob,
		}),
		copyText(text) {
			navigator.clipboard.writeText(text);
		},
		updateOptions(dropdownOptions) {
			this.urlParams = dropdownOptions.reduce((params, x) => ({ ...params, ...{ [x.text]: x.value } }), {});
		},
		editCustomURL(event, isUpgradeRequired, indicator) {
			if (isUpgradeRequired && indicator?.setup) {
				indicator.setup().openUpgrade(event, this.billingFeatureId);
			} else {
				AppDialogManagerService.openSetCustomDomainForWorkflowDialog({ workflow: this.workflow, plyLink: this.plyLink });
			}
		},
	},
	data() {
		return { urlParams: {} };
	},
	beforeDestroy() {
		this.setIsShowKnob(false);
		this.setIsSideBarDrawerOpen(false);
	},
	watch: {
		isCustomerAuthOn: {
			handler(newValue) {
				this.setIsSideBarDrawerOpen(newValue);
				this.setIsShowKnob(newValue);
			},
			immediate: true,
		},
	},
};
</script>

<style lang="scss" scoped>
.fs-14 {
	font-size: 14px;
}
.purple-text {
	color: #6162d8;
}
</style>
